import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

export type TogglePillTone = "violet" | "indigo";

const TONE_STYLE: Record<
  TogglePillTone,
  { base: string; pressed: string; idle: string }
> = {
  // The Assistant: the violet call to action, darker while its pane is open.
  // The ::before stretches the tap target to 44px without a taller pill.
  violet: {
    base: "relative h-[34px] gap-[7px] rounded-full border-transparent pr-4 pl-3 text-[12.5px] text-white shadow-assistant before:absolute before:inset-x-0 before:-inset-y-[5px] before:content-['']",
    pressed: "bg-linear-to-b from-[#6d28d9] to-[#5b21b6] pr-3",
    idle: "bg-linear-to-b from-[#7c3aed] to-[#6d28d9] hover:from-[#8b4cf0]",
  },
  indigo: {
    base: "h-8 gap-[7px] rounded-full border-transparent pr-2.5 pl-3 text-[12.5px] transition-colors motion-reduce:transition-none",
    pressed:
      "bg-indigo-50 text-indigo-700 shadow-[0_0_0_1px_rgba(23,150,148,.35)]",
    idle: "bg-linear-to-b from-white to-[#f6f9f9] text-ink shadow-pill hover:to-[#eef4f4]",
  },
};

// Toggle pill — a chip whose pressed state mirrors what it opens. Violet is
// the Assistant button; indigo is the Summary toggle, a quiet pill when closed.
// `pulse` rings it once to draw the eye.
export const TogglePill: FC<{
  pressed: boolean;
  onClick(): void;
  children: ReactNode;
  tone?: TogglePillTone;
  title?: string;
  pulse?: boolean;
}> = ({
  pressed,
  onClick,
  children,
  tone = "violet",
  title,
  pulse = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={pressed}
    title={title}
    className={clsx(
      "inline-flex shrink-0 items-center border font-semibold whitespace-nowrap",
      TONE_STYLE[tone].base,
      pressed ? TONE_STYLE[tone].pressed : TONE_STYLE[tone].idle,
      pulse && "summary-pulse",
    )}
  >
    {children}
  </button>
);
